import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import { X, Copy, RefreshCcw, FilePlus, SkipForward } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Duplicate Resolver modal — shown when a file's state is DUPLICATE.
 * Offers three actions backed by POST /sessions/{id}/resolve:
 *   - skip:        keep the existing statement, drop this one
 *   - replace:     drop the existing statement, import this one
 *   - new_version: import as a separate statement (history)
 *
 * Props:
 *  - sessionId
 *  - fileIndex
 *  - fileMeta (manifest entry)
 *  - onClose()
 *  - onResolved(updatedFile) — refresh trigger
 */
const ACTIONS = [
  {
    code: 'skip',
    label: 'تَجاوُز',
    desc:  'الإبقاء على الكَشف المَوجود وتَجاهُل هذا الملَف.',
    icon:  SkipForward,
    tone:  'bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border-zinc-300',
  },
  {
    code: 'replace',
    label: 'استِبدال',
    desc:  'حَذف الكَشف القَديم واستيراد هذا الملَف بَدَلاً عَنه (لا يَعود قابِلاً لِلتَّراجُع).',
    icon:  RefreshCcw,
    tone:  'bg-red-100 hover:bg-red-200 text-red-800 border-red-300',
  },
  {
    code: 'new_version',
    label: 'إصدار جَديد',
    desc:  'الاحتِفاظ بالنُّسخَتَين كَكَشفَين مُنفَصِلَين (تَتَبُّع تاريخي).',
    icon:  FilePlus,
    tone:  'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-300',
  },
];


export default function DuplicateResolver({ sessionId, fileIndex, fileMeta, onClose, onResolved }) {
  const [busy, setBusy] = useState(false);
  const dup = fileMeta?.duplicate;

  const apply = async (action) => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(
        `/bank-import/sessions/${sessionId}/resolve`,
        { file_index: fileIndex, action },
      );
      toast.success(`تَم تَطبيق الإجراء: ${ACTIONS.find((a) => a.code === action)?.label}`);
      onResolved?.(data?.file);
      onClose?.();
    } catch (e) {
      toast.error('فَشَل تَطبيق الإجراء');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dup-resolver">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl" dir="rtl">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-amber-600" />
            <div className="text-base font-bold">حَل التَّكرار</div>
          </div>
          <button onClick={onClose} data-testid="dup-close"
                  className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-amber-50/60 border border-amber-200 p-3 text-sm">
            <div className="font-semibold text-amber-900 mb-1">
              تَم اكتِشاف تَكرار بِنِسبَة {(dup?.similarity * 100 || 0).toFixed(0)}%
            </div>
            <div className="text-xs text-amber-800">
              الملَف <span className="font-mono">{fileMeta?.name}</span> مُكَرَّر مَع
              &nbsp;<span className="font-mono">{dup?.ref_filename || dup?.ref_id?.slice(0, 8)}</span>
              {dup?.ref_period && <> &middot; الفَترَة: {dup.ref_period}</>}
            </div>
            <div className="text-[10px] text-amber-700 mt-1">
              أَسباب: {(dup?.reasons || []).join(' / ')}
            </div>
          </div>

          <div className="space-y-2">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <button key={a.code} onClick={() => apply(a.code)} disabled={busy}
                        data-testid={`dup-action-${a.code}`}
                        className={`w-full text-start border rounded-lg p-3 transition flex items-start gap-2 disabled:opacity-50 ${a.tone}`}>
                  <Icon className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{a.label}</div>
                    <div className="text-xs opacity-80 mt-0.5">{a.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
